import { useMemo } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '@/store/authStore'
import { useQueryParams } from '@/utils/queryParams'
import { playApi } from '@/api/play'
import { apiClient } from '@/api/client'
import { AccessDenied } from '@/components/AccessDenied'
import { LoginTypeSelector } from '@/components/LoginTypeSelector'
import { NotFoundPage } from './NotFoundPage'
import { QueturePlayer } from '@quebon/shared-learning'
import type { QuetureApiInterface, QuetureResponse } from '@quebon/shared-learning'
import type { ApiResponse } from '@/types'

export function QuetureViewPage() {
  const { quetureId } = useParams<{ quetureId: string }>()
  const { wb, idx } = useQueryParams()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  if (!quetureId) return <NotFoundPage />

  if (!isAuthenticated) {
    if (wb && /^(KD|PJ|NJ)/.test(wb)) {
      return <LoginTypeSelector quetureId={quetureId} wb={wb} idx={idx} />
    }
    const params = new URLSearchParams()
    if (wb) params.set('wb', wb)
    if (idx) params.set('idx', idx)
    params.set('from', 'QR')
    const redirect = encodeURIComponent(`/queture/${quetureId}?${params.toString()}`)
    return <Navigate to={`/login?redirect=${redirect}`} replace />
  }

  return <QuetureCheckAndPlay quetureId={quetureId} wb={wb} />
}

function QuetureCheckAndPlay({ quetureId, wb }: { quetureId: string; wb: string | null }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['play-check', quetureId, wb],
    queryFn: () => playApi.checkAccess(quetureId, wb || undefined),
  })

  const quetureApiAdapter: QuetureApiInterface = useMemo(() => ({
    getQuetureInfo: async (qid: string): Promise<QuetureResponse> => {
      // Check API에서 contentId를 받았으면 사용, 없으면 qid 기반 fallback
      const contentId = data?.contentId
      if (contentId) {
        const response = await apiClient.get<ApiResponse<QuetureResponse>>(
          `/content/${contentId}/quetures/${qid}`
        )
        return response.data.data!
      }
      // contentId 없으면 에러 (Play앱에서는 contentId가 필요)
      throw new Error('contentId is required to fetch queture info')
    }
  }), [data?.contentId])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-white text-lg">로딩 중...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center space-y-2">
          <p className="text-gray-600">일시적인 오류가 발생했습니다</p>
          <button onClick={() => window.location.reload()} className="btn-primary px-4 py-2 text-sm">
            다시 시도
          </button>
        </div>
      </div>
    )
  }

  if (!data?.allowed) {
    return <AccessDenied message={data?.message} />
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="w-full max-w-5xl aspect-video">
        <QueturePlayer
          renderMetadataMath
          qid={quetureId}
          quetureApi={quetureApiAdapter}
          allowSkip={true}
          autoPlay={true}
        />
      </div>
    </div>
  )
}
